import { toastError } from '@onestep/coreui/Toast';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import { makeAutoObservable } from 'mobx';

import type { IAuthStore } from '../../auth/store';
import type { appStatusEnum } from '../../common/enums/patientAppStatus';
import type { IPatientQuestionnaire } from '../../services/questionnairesService/types';
import type { IMeeting } from '../Meetings/store';
import type { ITypeOfCare } from '../PatientDetailsPages/PatientUserDetails/store';
import type { IAppStore } from '../store';
import type { IGaitTrend } from './get-gait-trend';

export enum currentPlanType {
  basic,
  premium,
  plus = 10,
  hybrid = 4,
  ongoing = 5,
  science = 7,
  fullPt = 8,
  gaitLab = 9,
}

export enum currentPlanSubType {
  paid = 1,
  fakePaid = 2,
  subsidized = 3,
}

export interface IPatientPreview {
  therapistUuid: string;
  uuid: string;
  email: string;
  adherenceScore: string;
  firstName: string;
  lastName: string;
  firstLogin: number;
  localeTimezone: string;
  metricSystem: boolean;
  nextMeeting: number;
  primaryAgentUuid?: string;
  appStatus: appStatusEnum;
  address: string;
  phoneNumber: string;
  contactEmail: string;
  locationCodeIso3166: string;
  lastMeeting: number;
  fullName: string;
  profileImageUrl?: string;
  verifiedPhoneNumber?: string;
}

export enum CourseStatus {
  padding,
  inProgress,
  done,
}

export interface IPatientCourse {
  currentSession: number;
  title: string;
  uuid: string;
  status: CourseStatus;
}

export interface IMinimalPatientAgent {
  lastInappExercise: string;
  lastInappPerception: string;
  isIos: boolean;
}

export interface IPatientAgent extends IMinimalPatientAgent {
  lastInappExercise: string;
  lastInappPerception: string;
  status: number;
  uuid: string;
  versionCode: string;
  version: string;
  supportFcm: boolean;
  ntfInsights: boolean;
  remindersHour: number;
  remindersEnabled: boolean;
  lastInappSeen: string;
  lastSeen: string;
  registrationTime: string;
  lastRegistration: string;
  manufacturer: string;
  model: string;
  labels: string[];
}

export interface IPastPatientRowV2 {
  patientUuid: string;
  patientFullName: string;
  case?: string;
  emrId?: string;
  eocStart?: string | null;
  eocEnd?: string | null;
  eocUuid?: string | null;
  clinicians?: string[];
  clinics?: string[];
}

export interface IPastPatientRow {
  patientUuid: string;
  patientFullName: string;
  startDate: string;
  endDate: string;
  status: string;
  comment: string;
  therapistUuid: string;
  therapistFullName: string;
  clinicUuid: string;
  clinicName: string;
}

export interface ICurrentPlan {
  type: currentPlanType;
  subType?: currentPlanSubType;
  isActive?: boolean;
  isPaid?: false;
  createdAt: string;
  expiresAt: string;
  isAutoRenewing: boolean;
}

export interface IEvents {
  event: string;
  timestamp: string;
}

export interface ICallToAction {
  activityName: string;
  createdAt: string;
  endDate: string;
  isActive: boolean;
  priority: number;
  rule: string;
  startDate: string;
  taskActivity: string;
  taskTag: string;
  uuid: string;
}
export interface BaseFallRiskMetaData {
  name?: string;
  scale: {
    max: number;
    min: number;
  };
  thresholds: {
    max: number;
    min: number;
  };
  unit: string;
}

export interface IFallRiskParam extends BaseFallRiskMetaData {
  value: number;
}

export interface IFallRisk {
  explanation: string;
  explanationJson: { data: { [key: string]: IFallRiskParam } };
  score: number;
  date: string;
  id: number;
  priority?: number;
}

export interface IPatient {
  address?: string;
  clinicUuid: string;
  contactEmail?: string;
  countryCode: string;
  email: string;
  firstLogin: string;
  firstName: string;
  isGaitLab: boolean;
  isIos: boolean;
  isResearch: boolean;
  isTest: boolean;
  lastMeeting: string;
  lastName?: string;
  localeTimezone: string;
  metricSystem: boolean;
  mobilityAid: string;
  nextMeeting?: string;
  phoneNumber?: string;
  profileImageUrl?: string;
  therapistUuid: string;
  uuid: string;
  verifiedPhoneNumber?: string;
  dateOfBirth?: string;
  gender?: number;
  distributionChannelName?: string;
  accessibilityMode: boolean;
}

export interface IRtm {
  activeMonitorDays: number;
  lastUpdate: string;
  patientPeriodEnd: string;
  patientPeriodStart: string;
  startDate: string;
  therapistAggMinutes: number;
}

export interface ICurrentRtmCycle {
  activeDays: string[];
  end: string;
  start: string;
}
export interface IStats {
  inappSteps: number;
  inappWalks: number;
  inappWalksFull: number;
  monitorSteps: number;
  monitorStepsFull: number;
  monitorWalks: number;
  monitorWalksFull: number;
  sessionsDone: number;
  tasksDone: number;
  timestamp: string;
}

/**
 * @deprecated 27 mar, 2024 need to verify and delete
 */
export interface IChangeOfStatus {
  parameter: string;
  timestamp: string;
  msg?: string;
  statusChange: number;
}

export interface IClinicalDocuments {
  createdAt: string;
  docType: string;
  filename: string;
  uuid: string;
}

export interface IFeatureMap {
  dailyReminderHour: number;
  onBoarding: boolean;
  sendbird: boolean;
  fallRiskDismissedUntil?: string;
  accessibilityMode: boolean;
}

export interface ISurgeries {
  affectedSide: number;
  eventDate: string;
  id: number;
  name: string;
  note: string;
}

export interface IMeasurement {
  activityName: string;
  mainParam: number;
  mainParamDisplayName: string;
  mainParamUnits: string;
  perceiveTime: string;
  perceptionUuid: string;
  side: string;
}

export interface IWalkInPeriod {
  perceiveTime: string;
  walkScore: number;
}

export interface IPatientRow {
  patient: IPatient;
  events: IEvents[];
  clinicUuid?: string;
  callToActions: ICallToAction[];
  rtm: IRtm[];
  fallRisk: IFallRisk[];
  changeOfStatus: IChangeOfStatus;
  clinicalDocuments: IClinicalDocuments[];
  currentCourse?: IPatientCourse;
  nextCourse?: IPatientCourse;
  featureMap?: IFeatureMap;
  surgeries: ISurgeries[];
  tags: string[];
  doctorUuid?: string;
  lastMeasurements: IMeasurement[];
  lastQuestioners: IPatientQuestionnaire[];
  plan?: ICurrentPlan;
  stats?: IStats;
  config?: {
    remindersEnabled: boolean;
  };
  distributionChannel?: {
    displayName: string;
    profileImageUrl: string;
  };
  meetings?: IMeeting[];
  latestFallRisk?: IFallRisk;
  therapistUuid?: string;
  shouldShowIntake: boolean;
  therapistTime: { [key: string]: number };
  latestFall: string;
  walksInPeriod: IWalkInPeriod[];
  lastReportedMeeting: string;
  currentEpisodeOfCare: ITypeOfCare;
  gaitTrend: IGaitTrend;
  currentRtmCycle: ICurrentRtmCycle;
}

export interface IPatientsStore {
  patientResourcesQuery: string[] | null;

  updateWorkspace(
    patientUuid: string,
    workspaceUuid: string,
    errorHandler?: { [key in StatusCodes]?: () => void },
  ): Promise<void | boolean>;

  setResources(resources: string[] | null): void;
}

export default class PatientsStore implements IPatientsStore {
  appStore: IAppStore;
  authStore: IAuthStore;

  patientResourcesQuery: string[] | null = null;

  constructor(appStore: IAppStore, authStore: IAuthStore) {
    makeAutoObservable(this);

    this.authStore = authStore;
    this.appStore = appStore;
  }

  setResources(resources: string[] | null) {
    this.patientResourcesQuery = resources;
  }

  async updateWorkspace(
    patientUuid: string,
    workspaceUuid: string,
    errorHandler?: { [key in StatusCodes]?: () => void | boolean },
  ): Promise<void | boolean> {
    try {
      return await axios.put(`/api/camel/patients/assign_workspace/${patientUuid}`, {
        workspaceUuid,
      });
    } catch (e) {
      if (e.response?.status === StatusCodes.CONFLICT) {
        if (errorHandler?.[StatusCodes.CONFLICT]) {
          return errorHandler?.[StatusCodes.CONFLICT]?.();
        } else {
          toastError({
            content:
              'Failed to move, Patient current therapist does not have permissions to selected workspace',
          });
          return false;
        }
      }
      toastError({ content: 'Failed to update workspace' });
      return false;
    }
  }
}
